import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { useAuthStore } from '../../state/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { supabase, supabaseEnabled } from '../../lib/supabase';

/** Target for the Supabase "Reset Password" email link.
 *  Supabase puts a recovery access token in the URL fragment; supabase-js picks
 *  it up automatically, emits a PASSWORD_RECOVERY event, and establishes a
 *  short-lived session we can use to call updateUser({ password }). */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const setNewPassword = useAuthStore((s) => s.setNewPassword);
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!supabaseEnabled) return;
    // supabase-js fires PASSWORD_RECOVERY once it consumes the URL fragment.
    // Poll for a session instead of subscribing (simpler; same timing).
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 20 && !cancelled; i += 1) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) { setReady(true); return; }
        await new Promise((r) => setTimeout(r, 150));
      }
      if (!cancelled) {
        setError('This reset link is invalid or has expired. Request a new one.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!supabaseEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
        <Card className="w-full max-w-md border-gray-800 bg-gray-900">
          <CardContent className="p-8 text-center">
            <p className="text-sm text-gray-400">Password reset is not enabled on this deployment.</p>
            <Button onClick={() => navigate('/login')} className="mt-4 bg-emerald-600 hover:bg-emerald-700">Back to sign in</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords don\u2019t match.');
      return;
    }
    setSaving(true);
    const result = await setNewPassword(password);
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not update password. Request a new reset link and try again.');
      return;
    }
    setSaved(true);
    setTimeout(() => navigate('/login', { replace: true }), 1500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
      <Card className="w-full max-w-md border-gray-800 bg-gray-900">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-xl bg-emerald-600/20">
              <Activity className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-100">Set a new password</CardTitle>
          <CardDescription className="text-gray-400">
            Pick something strong. You&rsquo;ll be signed in after saving.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {saved ? (
            <p className="text-sm text-emerald-400 text-center">Password updated. Redirecting you to sign in\u2026</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="border-red-800 bg-red-950">
                  <AlertDescription className="text-red-300">{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label className="text-gray-300">New password</Label>
                <Input
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!ready}
                  required
                  className="bg-gray-800 border-gray-700 text-gray-100"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Confirm password</Label>
                <Input
                  type="password"
                  placeholder="Repeat your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={!ready}
                  required
                  className="bg-gray-800 border-gray-700 text-gray-100"
                />
              </div>
              <Button
                type="submit"
                disabled={!ready || saving}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? 'Saving\u2026' : ready ? 'Update password' : 'Preparing\u2026'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
